/**
 * Dashboard — visão de operação.  Mostra todas as geladeiras visíveis
 * pro user atual com estado em tempo (quase) real.
 */

import { useEffect, useMemo, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Maximize2, Monitor, Snowflake } from "lucide-react";

import { equipmentsApi } from "@/api/equipments";
import {
  qk,
  useActiveAlerts,
  useEquipments,
  useSites,
  useTenants,
} from "@/hooks/queries";
import { useRotuloDeUnidade } from "@/hooks/useRotuloDeUnidade";
import { useAuth } from "@/contexts/AuthContext";
import type { Severity } from "@/types";

import { PageHeader } from "@/components/PageHeader";
import { EquipmentCard } from "@/components/EquipmentCard";
import { Loading } from "@/components/Loading";
import { EmptyState } from "@/components/EmptyState";
import { Skeleton } from "@/components/Skeleton";
import { Badge } from "@/components/Badge";
import { Field, Select } from "@/components/Field";
import { SitePowerOutageBanner } from "@/components/SitePowerOutageBanner";
import { TenantAIHealthCard } from "@/components/TenantAIHealthCard";
import { PlantaDoSite } from "@/components/PlantaDoSite";
import { TenantStatsCard } from "@/components/TenantStatsCard";
import { evaluateTemperature, type SetpointConfig } from "@/lib/thresholds";
import { SEVERIDADE_RANK } from "@/lib/severidade";

export function DashboardPage() {
  const auth = useAuth();
  const equipments = useEquipments();
  const activeAlerts = useActiveAlerts();

  // Filtros (só visíveis pra super_admin — tenants/sites são contexto único
  // pra qualquer outro role, não fazem sentido como filtro)
  const tenants = useTenants(auth.isSuperAdmin);
  const sites = useSites();
  const { opcoes: opcoesDeUnidade } = useRotuloDeUnidade();

  const [sitePlanta, setSitePlanta] = useState<string | null>(null);
  const [tenantFilter, setTenantFilter] = useState("");
  const [siteFilter, setSiteFilter] = useState("");

  // Sites disponíveis no dropdown — filtrados pelo tenant selecionado
  const visibleSiteOptions = useMemo(() => {
    let list = sites.data ?? [];
    if (tenantFilter) list = list.filter((s) => s.tenant_id === tenantFilter);
    return list;
  }, [sites.data, tenantFilter]);

  // Se trocou o tenant e o site selecionado não pertence mais a ele, limpa
  useEffect(() => {
    if (siteFilter && !visibleSiteOptions.some((s) => s.id === siteFilter)) {
      setSiteFilter("");
    }
  }, [siteFilter, visibleSiteOptions]);

  /**
   * Unidades cuja planta pode aparecer aqui.
   *
   * Isto é `visibleSiteOptions`, o mesmo recorte do seletor de Site, e não a
   * lista crua do `useSites` — que era o bug: o super_admin com "Cliente 01"
   * escolhido continuava vendo, nos chips da planta, as unidades de TODOS os
   * clientes, e trocava pra unidade de outro cliente sem sair do painel dele.
   *
   * Com "Todos" selecionado a lista fica vazia de propósito. Planta é de uma
   * unidade; não existe "planta de todos os clientes", e o card subia pro
   * topo da página porque os indicadores acima dele não renderizam sem
   * tenant — a planta virava a primeira coisa do dashboard.
   */
  const plantasVisiveis = useMemo(() => {
    if (auth.isSuperAdmin && !tenantFilter) return [];
    // Cliente com o recurso desligado não tem chips nem link — o card
    // sozinho sabe se esconder, mas a moldura em volta dele não.
    const comPlanta = (visibleSiteOptions ?? []).filter(
      (s) => s.plantas_habilitadas !== false);
    if (!comPlanta.length) return [];
    // Com uma unidade escolhida no filtro, a planta é a dela — o filtro do
    // topo não pode dizer uma coisa e o card embaixo mostrar outra.
    if (siteFilter) return comPlanta.filter((s) => s.id === siteFilter);
    return comPlanta;
  }, [auth.isSuperAdmin, tenantFilter, siteFilter, visibleSiteOptions]);

  // Sem escolha explícita, a primeira. E a escolha morre quando sai da lista:
  // trocar de cliente não pode deixar a planta do cliente anterior na tela.
  const sitePlantaAtual =
    (sitePlanta && plantasVisiveis.some((s) => s.id === sitePlanta) ? sitePlanta : null)
    ?? plantasVisiveis[0]?.id
    ?? null;

  /** O cliente que o painel de parede vai mostrar. */
  const tenantDoPainel = auth.isSuperAdmin
    ? (tenantFilter || null)
    : (auth.user?.tenant_id ?? null);

  // Map de site_id → tenant_id pra filtrar equipments por tenant (Equipment
  // só carrega site_id, então precisa joinar com sites)
  const tenantBySite = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of sites.data ?? []) m.set(s.id, s.tenant_id);
    return m;
  }, [sites.data]);

  // Lista filtrada de equipments
  const list = useMemo(() => {
    let l = equipments.data ?? [];
    if (tenantFilter) {
      l = l.filter((e) => tenantBySite.get(e.site_id) === tenantFilter);
    }
    if (siteFilter) {
      l = l.filter((e) => e.site_id === siteFilter);
    }
    return l;
  }, [equipments.data, tenantFilter, siteFilter, tenantBySite]);

  // Map equipment_id → maior severity firing (critical > warning > info)
  const severityByEquipment = useMemo(() => {
    return buildSeverityMap(activeAlerts.data ?? []);
  }, [activeAlerts.data]);

  // Query múltipla: pra cada equipment (já filtrado), pega o summary.
  // TanStack faz dedup e cache, e o refetchInterval mantém atualizado.
  //
  // 10s pollin no dashboard — equilíbrio: usuario quer ver mudanças logo,
  // mas N cards × 10s já gera carga linear. Se ficar pesado em prod com
  // muitos ativos, partir pro SSE (estratégia D do plano de latência).
  const summaries = useQueries({
    queries: list.map((eq) => ({
      queryKey: qk.equipmentSummary(eq.id),
      queryFn: () => equipmentsApi.summary(eq.id),
      refetchInterval: 10_000,
      staleTime: 5_000,
    })),
  });

  if (equipments.isLoading) return <Loading label="Buscando ativos..." />;

  // Métricas top-of-page (overview).
  const validSummaries = summaries
    .map((q) => q.data)
    .filter((d): d is NonNullable<typeof d> => Boolean(d));

  const counts = { ok: 0, warn: 0, alert: 0, stale: 0 };
  validSummaries.forEach((s) => {
    const cfg = (s.metadata as SetpointConfig) ?? {};
    const v =
      s.metrics.temp_internal ?? s.metrics.temp_product ?? null;
    const r = evaluateTemperature(v, cfg, s.last_seen);
    if (r.status === "ok")    counts.ok++;
    if (r.status === "warn")  counts.warn++;
    if (r.status === "alert") counts.alert++;
    if (r.status === "stale") counts.stale++;
  });

  return (
    <div className="mx-auto max-w-7xl">
      <PageHeader
        title={`Olá, ${auth.user?.name ?? auth.user?.email.split("@")[0] ?? ""}`}
        description="Estado atual dos ativos."
        actions={
          /*
           * "Painel de todas as unidades", e não "Modo painel".
           *
           * São DOIS painéis — o do cliente inteiro e o de uma unidade — e
           * os dois botões tinham o mesmo rótulo. Quem desenhava a planta de
           * uma unidade e clicava no botão desta tela caía no painel do
           * cliente, sem planta nenhuma, e concluía que o desenho não tinha
           * sido salvo. Rótulo que não diz o destino é pior que rótulo
           * comprido.
           *
           * Sem cliente definido não existe painel: ele é de UM cliente, e o
           * super_admin sem filtro não escolheu nenhum.
           */
          tenantDoPainel && (
            <Link
              to={`/kiosk/tenants/${tenantDoPainel}`}
              // Nova aba: o painel é pra ficar aberto, muitas vezes numa
              // segunda tela. Substituir a aba de trabalho por ele obriga a
              // refazer o caminho todo pra voltar ao que se estava fazendo.
              target="_blank"
              rel="noopener"
              aria-label="Painel de todas as unidades"
              title="Painel de todas as unidades"
              className="rounded-lg border border-surface-200 p-2 text-slate-500 hover:bg-surface-100 hover:text-slate-700 dark:border-slate-800 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
            >
              <Monitor className="h-4 w-4" aria-hidden />
            </Link>
          )
        }
      />

      {/* #162: Camada 2 site_power_outage — banner no topo, esconde se vazio */}
      <div className="mb-4">
        <SitePowerOutageBanner />
      </div>

      {/* Filtros (apenas pra super_admin — outros roles têm tenant fixo) */}
      {auth.isSuperAdmin && (
        <div className="mb-4 grid gap-3 sm:grid-cols-2">
          <Field label="Tenant">
            <Select
              value={tenantFilter}
              onChange={(e) => setTenantFilter(e.target.value)}
              options={[
                { value: "", label: "Todos" },
                ...(tenants.data ?? []).map((t) => ({
                  value: t.id,
                  label: t.name,
                })),
              ]}
            />
          </Field>
          <Field label="Site">
            <Select
              value={siteFilter}
              onChange={(e) => setSiteFilter(e.target.value)}
              options={[
                { value: "", label: "Todos" },
                ...opcoesDeUnidade(visibleSiteOptions),
              ]}
              disabled={visibleSiteOptions.length === 0}
            />
          </Field>
        </div>
      )}

      {/* Indicadores agregados do tenant.
          super_admin sem filtro de tenant → componente esconde sozinho.
          tenant users → usa o próprio tenant_id. */}
      <div className="mb-6">
        <TenantStatsCard
          tenantId={
            auth.isSuperAdmin
              ? (tenantFilter || undefined)
              : (auth.user?.tenant_id ?? undefined)
          }
        />
      </div>

      {/* Planta da unidade.
          Planta é de UNIDADE FÍSICA, não de cliente — um cliente com cinco
          lojas tem cinco plantas. Por isso o seletor: o dashboard agrega o
          tenant, mas a planta só existe no singular. Com uma unidade só, o
          seletor some e ela aparece direto. */}
      {plantasVisiveis.length > 0 && (
        <div className="mb-6">
          {plantasVisiveis.length > 1 && (
            <div className="mb-2 flex flex-wrap gap-1.5">
              {plantasVisiveis.map((st) => (
                <button
                  key={st.id}
                  type="button"
                  onClick={() => setSitePlanta(st.id)}
                  className={
                    "rounded-full border px-3 py-1 text-xs font-medium " +
                    (sitePlantaAtual === st.id
                      ? "border-brand-500 bg-brand-500 text-white"
                      : "border-surface-300 text-slate-600 dark:border-slate-700 dark:text-slate-300")
                  }
                >
                  {st.name}
                </button>
              ))}
            </div>
          )}
          {sitePlantaAtual && (
            <>
              <PlantaDoSite siteId={sitePlantaAtual} editavel colapsavel />
              {/* A planta aqui divide espaço com o dashboard do cliente
                  inteiro. A tela da unidade é o lugar onde ela é o assunto —
                  e é de lá que se abre o modo painel. */}
              {/* Dois destinos, e os dois daqui: a unidade já está
                  escolhida nos chips acima, então obrigar a passar pela tela
                  dela pra chegar no painel dela é um salto a mais sem
                  ganho. */}
              {/* Só o ícone: o rótulo repetia o que o card já diz, e o
                  destino é uma tela, não uma ação — ela se explica sozinha
                  quando abre. */}
              <div className="mt-2 flex items-center justify-end">
                <Link
                  to={`/kiosk/sites/${sitePlantaAtual}`}
                  target="_blank"
                  rel="noopener"
                  aria-label="Painel desta unidade"
                  title="Painel desta unidade"
                  className="rounded-lg border border-surface-200 p-2 text-slate-500 hover:bg-surface-100 hover:text-slate-700 dark:border-slate-800 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                >
                  <Maximize2 className="h-4 w-4" aria-hidden />
                </Link>
              </div>
            </>
          )}
        </div>
      )}

      {/* envpulse AI — saúde geral. Mesma regra de visibilidade do StatsCard. */}
      <div className="mb-6">
        <TenantAIHealthCard
          tenantId={
            auth.isSuperAdmin
              ? (tenantFilter || undefined)
              : (auth.user?.tenant_id ?? undefined)
          }
        />
      </div>

      {/* Overview */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <OverviewTile
          label="Total"
          value={list.length}
          icon={<Snowflake className="h-5 w-5 text-brand-600" />}
          tone="brand"
        />
        <OverviewTile label="OK"        value={counts.ok}    tone="success" />
        <OverviewTile label="Atenção"   value={counts.warn}  tone="warning" />
        <OverviewTile label="Críticos + sem dados" value={counts.alert + counts.stale} tone="danger" />
      </div>

      {/* Grid */}
      {list.length === 0 ? (
        <EmptyState
          title={
            tenantFilter || siteFilter
              ? "Nenhum ativo no filtro"
              : "Nenhum ativo cadastrado"
          }
          description={
            tenantFilter || siteFilter
              ? "Ajuste o filtro de tenant/site ou limpe pra ver todas."
              : "Crie um site, depois cadastre os ativos com seus devices físicos."
          }
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {list.map((eq, i) => {
            const summary = summaries[i]?.data;
            if (!summary) {
              return <Skeleton key={eq.id} className="h-48 w-full rounded-xl" />;
            }
            return (
              <EquipmentCard
                key={eq.id}
                summary={summary}
                alertSeverity={severityByEquipment.get(eq.id) ?? null}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------- helpers ---------------------------------------------------------

const SEV_RANK = SEVERIDADE_RANK;

/**
 * Reduz uma lista de active alerts em um map equipment_id → severity mais alta.
 * critical sobrepõe warning sobrepõe info.
 */
function buildSeverityMap(
  alerts: { equipment_id: string; severity: Severity }[],
): Map<string, Severity> {
  const m = new Map<string, Severity>();
  for (const a of alerts) {
    const cur = m.get(a.equipment_id);
    if (!cur || SEV_RANK[a.severity] > SEV_RANK[cur]) {
      m.set(a.equipment_id, a.severity);
    }
  }
  return m;
}

function OverviewTile({
  label,
  value,
  icon,
  tone = "default",
}: {
  label: string;
  value: number;
  icon?: React.ReactNode;
  tone?: "brand" | "success" | "warning" | "danger" | "default";
}) {
  return (
    <div className="rounded-xl border border-surface-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 shadow-card">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {label}
        </span>
        {icon}
      </div>
      <div className="mt-2 flex items-end gap-2">
        <div className="text-3xl font-bold tabular-nums text-slate-900 dark:text-slate-100">{value}</div>
        {tone !== "default" && (
          <Badge
            tone={tone === "brand" ? "brand" : tone}
            className="mb-1"
          >
            {label}
          </Badge>
        )}
      </div>
    </div>
  );
}
